/**
 * Brand-Logo-Mapping: Marke → offizielle Hersteller-Domain.
 *
 * Wird von `<BrandLogo>` genutzt, um über Google's Favicon-Service (s2 API)
 * das offizielle Marken-Logo in beliebiger Auflösung (16/32/64/128/256 px)
 * zu rendern. Vorteile gegenüber lokalem Hosting:
 *  - Keine Lizenz-/Markenrechts-Probleme (Logo kommt direkt vom Hersteller)
 *  - Immer up-to-date wenn Hersteller Logo wechselt
 *  - CDN-cachable, kostenfrei, kein Server-Roundtrip
 *  - Funktioniert offline-fallback via Initial-Buchstabe
 *
 * Eintrag fehlt? `<BrandLogo>` zeigt automatisch die Fallback-Initial-Variante.
 *
 * Erweiterung: einfach neue Marken-Domain unten anhängen — sortiert nach
 * Kategorie (Motorrad/Roller-Crossover/E-Bike).
 */

// MOTORRÄDER + ROLLER (alle MOTO_BRANDS aus listingConstants.js)
export const MOTO_BRAND_DOMAINS = {
    "Aprilia": "aprilia.com",
    "Benelli": "benelli.com",
    "Beta": "betamotor.com",
    "BMW": "bmw-motorrad.com",
    "Bimota": "bimota.it",
    "Buell": "buellmotorcycle.com",
    "Cagiva": "cagiva.com",
    "CFMoto": "cfmoto.com",
    "Ducati": "ducati.com",
    "Energica": "energicamotor.com",
    "Fantic": "fantic.com",
    "GasGas": "gasgas.com",
    "Harley-Davidson": "harley-davidson.com",
    "Honda": "honda.de",
    "Husaberg": "husaberg.com",
    "Husqvarna": "husqvarna-motorcycles.com",
    "Indian": "indianmotorcycle.com",
    "Kawasaki": "kawasaki.de",
    "KTM": "ktm.com",
    "Kymco": "kymco.com",
    "Laverda": "laverda.com",
    "MBK": "mbk.fr",
    "Moto Guzzi": "motoguzzi.com",
    "Moto Morini": "motomorini.com",
    "MV Agusta": "mvagusta.com",
    "Norton": "nortonmotorcycles.com",
    "NSU": "audi-tradition.de",
    "Peugeot Motocycles": "peugeot-motocycles.de",
    "Piaggio": "piaggio.com",
    "Polaris": "polaris.com",
    "Puch": "puch.at",
    "Quadro": "quadrovehicles.com",
    "Rieju": "rieju.es",
    "Royal Enfield": "royalenfield.com",
    "Sherco": "sherco.com",
    "Suzuki": "suzuki-motorcycles.de",
    "SWM": "swm-motorcycles.com",
    "SYM": "sym-global.com",
    "Triumph": "triumphmotorcycles.de",
    "Ural": "imz-ural.com",
    "Vespa": "vespa.com",
    "Victory": "victorymotorcycles.com",
    "Voge": "voge-motor.com",
    "Yamaha": "yamaha-motor.eu",
    "Zero Motorcycles": "zeromotorcycles.com",
};

// FAHRRÄDER (E-Bike + klassisch)
export const BIKE_BRAND_DOMAINS = {
    "Bergamont": "bergamont.com",
    "Bianchi": "bianchi.com",
    "BMC": "bmc-switzerland.com",
    "Bulls": "bulls.de",
    "Canyon": "canyon.com",
    "Cannondale": "cannondale.com",
    "Centurion": "centurion.de",
    "Conway": "conway-bikes.de",
    "Cube": "cube.eu",
    "Diamant": "diamantrad.com",
    "Felt": "feltbicycles.com",
    "Focus": "focus-bikes.com",
    "Gazelle": "gazelle.de",
    "Ghost": "ghost-bikes.com",
    "Giant": "giant-bicycles.com",
    "Haibike": "haibike.com",
    "Kalkhoff": "kalkhoff-bikes.com",
    "Kona": "konaworld.com",
    "KTM Fahrrad": "ktm-bikes.at",
    "Look": "lookcycle.com",
    "Merida": "merida-bikes.com",
    "Orbea": "orbea.com",
    "Pegasus": "pegasus-bikes.de",
    "Pinarello": "pinarello.com",
    "Radon": "radon-bikes.de",
    "Riese & Müller": "r-m.de",
    "Rose": "rosebikes.de",
    "Santa Cruz": "santacruzbicycles.com",
    "Scott": "scott-sports.com",
    "Specialized": "specialized.com",
    "Stevens": "stevensbikes.de",
    "Storck": "storck-bicycle.de",
    "Tern": "ternbicycles.com",
    "Trek": "trekbikes.com",
    "Trinx": "trinxbike.com",
    "Velo de Ville": "velo-de-ville.com",
    "Winora": "winora.de",
};

export const ALL_BRAND_DOMAINS = { ...MOTO_BRAND_DOMAINS, ...BIKE_BRAND_DOMAINS };

/**
 * Manuelle Logo-Overrides für Marken, bei denen Clearbit nur einen generischen
 * Globus / kein Logo liefert. URLs zeigen auf öffentlich nutzbare SVG/PNG-
 * Dateien (Wikimedia Commons). Wikimedia liefert direkte Hotlinks für SVG
 * über `upload.wikimedia.org`.
 *
 * Feb-2026 (User-Wunsch 27 May): Logos für Vespa, Piaggio, Suzuki, Moto Guzzi,
 * MV Agusta, Aprilia, Triumph, Harley-Davidson, Royal Enfield, Polaris, Victory,
 * Zero, Voge etc. explizit setzen — Clearbit hatte hier Lücken.
 */
export const MANUAL_BRAND_LOGOS = {
    "Vespa": "https://upload.wikimedia.org/wikipedia/commons/d/dd/Vespa_logo.svg",
    "Piaggio": "https://upload.wikimedia.org/wikipedia/commons/8/8e/Piaggio-logo.svg",
    "Suzuki": "https://upload.wikimedia.org/wikipedia/commons/3/30/Suzuki_logo_2.svg",
    "Moto Guzzi": "https://upload.wikimedia.org/wikipedia/commons/f/fb/Moto_Guzzi_logo.svg",
    "MV Agusta": "https://upload.wikimedia.org/wikipedia/commons/3/35/MV_Agusta_Logo.svg",
    "Aprilia": "https://upload.wikimedia.org/wikipedia/commons/c/c0/Aprilia_logo.svg",
    "Triumph": "https://upload.wikimedia.org/wikipedia/commons/b/b6/Logo_Triumph_Motorcycles.svg",
    "Royal Enfield": "https://upload.wikimedia.org/wikipedia/commons/d/df/Royal_Enfield_logo.svg",
    "Polaris": "https://upload.wikimedia.org/wikipedia/commons/0/00/Polaris_Industries_logo.svg",
    "Victory": "https://upload.wikimedia.org/wikipedia/commons/e/e8/Victory_Motorcycles_logo.svg",
    "Zero Motorcycles": "https://upload.wikimedia.org/wikipedia/commons/6/6f/Zero_Motorcycles_logo.svg",
    "Voge": "https://upload.wikimedia.org/wikipedia/commons/0/06/Voge_Motor_logo.png",
    "Peugeot Motocycles": "https://upload.wikimedia.org/wikipedia/commons/3/3d/Peugeot_Motocycles_logo.svg",
    "Sherco": "https://upload.wikimedia.org/wikipedia/commons/8/86/Sherco_logo.svg",
    "MBK": "https://upload.wikimedia.org/wikipedia/commons/7/7c/MBK_Industrie_logo.svg",
    "Moto Morini": "https://upload.wikimedia.org/wikipedia/commons/9/9f/Moto_Morini_logo.svg",
    "SWM": "https://upload.wikimedia.org/wikipedia/commons/1/13/SWM_Motorcycles_logo.svg",
    "SYM": "https://upload.wikimedia.org/wikipedia/commons/3/30/SYM_Motors_logo.svg",
    "Quadro": "https://upload.wikimedia.org/wikipedia/commons/3/38/Quadro_Vehicles_logo.png",
    "Rieju": "https://upload.wikimedia.org/wikipedia/commons/4/4f/Rieju_logo.png",
    "Ural": "https://upload.wikimedia.org/wikipedia/commons/0/0e/Ural_Motorcycles_logo.svg",
    "Laverda": "https://upload.wikimedia.org/wikipedia/commons/9/96/Laverda_logo.svg",
    "Puch": "https://upload.wikimedia.org/wikipedia/commons/8/87/Puch_logo.svg",
    "NSU": "https://upload.wikimedia.org/wikipedia/commons/5/5d/NSU_Motorenwerke_logo.svg",
    "Bimota": "https://upload.wikimedia.org/wikipedia/commons/4/47/Bimota_logo.svg",
};

/**
 * Build a logo URL for a brand. Strategy:
 *  Stage 0 — Clearbit Logo API (hochauflösende SVG/PNG, deckt ~70% ab)
 *  Stage 1 — Manuelles Override (Wikimedia SVG) für Marken ohne Clearbit-Treffer
 *  Stage 2 — Yellow Initial-Tile Fallback (in BrandLogo Component)
 *
 * Google's Favicon API (s2) wurde entfernt — sie lieferte für unbekannte
 * Domains nur generische Globus-Icons, was visuell deutlich schlechter ist
 * als das gelbe Initial-Tile.
 *
 * @param {string} brand — exact spelling from MOTO_BRANDS / BIKE_BRANDS
 * @param {number} size — pixel size (32 | 64 | 128 | 256)
 */
export function brandLogoUrl(brand, size = 64) {
    const domain = ALL_BRAND_DOMAINS[brand];
    if (!domain) return null;
    return `https://logo.clearbit.com/${encodeURIComponent(domain)}?size=${Math.max(64, size)}`;
}

/**
 * Fallback Logo-URL — gibt das manuell hinterlegte Wikimedia-Logo zurück
 * (falls vorhanden). Returns null wenn kein Override existiert → BrandLogo
 * springt direkt zum Initial-Tile.
 */
export function brandLogoFallbackUrl(brand) {
    return MANUAL_BRAND_LOGOS[brand] || null;
}
